import {Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2} from "@angular/core";
import {lastValueFrom} from "rxjs";
import {PerFormaAPIService} from "src/app/services/pfapi.service";

// fetches a scan page thumbnail through the authenticated api client and binds it to the host <img>
// as an object url. the scan-image endpoint requires a bearer token, so a raw <img src> can't load
// it. inside the cdk virtual scroll the directive is created/recycled per rendered item, so only the
// thumbnails actually scrolled into the viewport are fetched — the full page set is never pulled.
@Directive({
    selector: "img[appScanThumb]",
    standalone: false
})
export class ScanThumbDirective implements OnChanges, OnDestroy {
    @Input("appScanThumb") scanPageId?: number;

    private objectUrl?: string;
    // guards against an in-flight fetch resolving after the view was recycled to another page.
    private requestId = 0;

    constructor(
        private readonly el: ElementRef<HTMLImageElement>,
        private readonly renderer: Renderer2,
        private readonly api: PerFormaAPIService
    ) {}

    async ngOnChanges(): Promise<void> {
        const requested = ++this.requestId;
        this.revoke();
        this.renderer.removeAttribute(this.el.nativeElement, "src");
        if (!this.scanPageId) return;
        try {
            const blob = await lastValueFrom(
                this.api.getApiScanImage("image/jpeg", {scanPageId: this.scanPageId, mini: true})
            );
            // a newer change recycled this view while the fetch was in flight — drop the stale blob.
            if (requested !== this.requestId) return;
            this.objectUrl = URL.createObjectURL(blob);
            this.renderer.setAttribute(this.el.nativeElement, "src", this.objectUrl);
        } catch {
            // a missing thumbnail (404) leaves the image blank — non-fatal.
        }
    }

    ngOnDestroy(): void {
        this.revoke();
    }

    private revoke(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = undefined;
        }
    }
}
